'use client';

import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

export function useModalFocusTrap<T extends HTMLElement>() {
	const dialogRef = useRef<T | null>(null);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;

		const previousActiveElement =
			document.activeElement instanceof HTMLElement
				? document.activeElement
				: null;
		const focusable = Array.from(
			dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
		).filter((element) => !element.hasAttribute('disabled'));
		(focusable[0] ?? dialog).focus();

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Tab') return;

			const currentFocusable = Array.from(
				dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
			).filter((element) => !element.hasAttribute('disabled'));
			if (currentFocusable.length === 0) {
				event.preventDefault();
				dialog.focus();
				return;
			}

			const first = currentFocusable[0];
			const last = currentFocusable.at(-1);
			if (!first || !last) return;

			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			previousActiveElement?.focus();
		};
	}, []);

	return dialogRef;
}
